import React from 'react';
import { Button, Col, Drawer, Row, Table, Tag } from 'antd';
import { useState, useEffect } from 'react';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { fetchSectionItems, applySorting } from 'services/settings/lookup_table/section.jsx';
import SectionForm from './sectionForm.jsx';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import DragableBodyRow from 'shared_components/drag_and_drop_row';
import { showMessage } from 'services/settings/message';

function Section() {
	const [section, setSection] = useState([]);
	const [hasReordered, setHasReordered] = useState(false);
	const [drawerState, setDrawerState] = useState({
		visible: false,
		drawerTitle: '',
		selecetedData: null,
		buttonNames: '',
	});

	useEffect(() => {
		getSections();
	}, []);

	const getSections = async () => {
		const data = await fetchSectionItems();

		// Sort by sectionSort ASC, but put records without sectionSort at the end
		const sortedData = (data || []).sort((a, b) => {
			// If both have sectionSort, sort normally
			if (a.sectionSort != null && b.sectionSort != null) {
				return a.sectionSort - b.sectionSort;
			}
			// If only a has no sectionSort, put it at the end
			if (a.sectionSort == null) return 1;
			// If only b has no sectionSort, put it at the end
			if (b.sectionSort == null) return -1;
			return 0;
		});

		setSection(sortedData);
	};

	const onSuccessCreate = async () => {
		onDrawerClose();
		await getSections();
		showMessage('M0002');
	};

	const onSuccessUpdate = async () => {
		onDrawerClose();
		await getSections();
		showMessage('M0003');
	};

	const applySort = async () => {
		const dataPayload = section.map((rec, index) => {
			return {
				sectionID: rec.sectionID,
				sectionSort: index + 1,
			};
		});

		await applySorting(dataPayload);
		setHasReordered(false);
	};

	const moveRow = (dragIndex, hoverIndex) => {
		const dragRow = section[dragIndex];

		const updatedData = update({ section }, {
			section: { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] }
		});

		setSection(updatedData.section);
		setHasReordered(true);
	};

	const showDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'ADD SECTION',
			selecetedData: null,
			buttonNames: 'ADD',
		});
	};

	const displayDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'UPDATE SECTION',
			selecetedData: record,
			buttonNames: 'UPDATE',
		});
	};

	const onDrawerClose = () => {
		setDrawerState({
			visible: false,
			drawerTitle: '',
			selecetedData: null,
			buttonNames: '',
		});
	};

	const components = {
		body: {
			row: DragableBodyRow,
		},
	};

	const columns = [
		{
			title: 'SECTION NAME',
			dataIndex: 'sectionName',
			width: 300,
			sorter: (a, b) => { return a.sectionName.localeCompare(b.sectionName) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'SECTION CODE',
			dataIndex: 'sectionCode',
			width: 200,
			sorter: (a, b) => { return a.sectionCode.localeCompare(b.sectionCode) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'DATE CREATED',
			dataIndex: 'createdDate',
			width: 200,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'STATUS',
			dataIndex: 'isActive',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? 'red' : 'green'}>{active === 0 ? 'INACTIVE' : 'ACTIVE'}</Tag>
			),
		},
	];

	return (
		<>
			<Row justify="end" style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: '10px' }}>
				<DownloadCSV
					confirmRequired={true}
					isLookupTable={true}
					confirmDetails={{
						section: 'SECTIONS',
						count: section.length
					}}
					data={section}
					filename="sections"
					columns={[
						{ title: 'SECTION NAME', dataIndex: 'sectionName' },
						{ title: 'SECTION CODE', dataIndex: 'sectionCode' },
						{ 
							title: 'DATE CREATED', 
							dataIndex: 'createdDate',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'STATUS', 
							dataIndex: 'isActive',
							render: active => active === 0 ? 'INACTIVE' : 'ACTIVE'
						}
					]}
				/>
				<Button
					onClick={applySort}
					shape="round"
					type="primary"
					disabled={!hasReordered}
				>
					APPLY SORTING
				</Button>
				<Button
					// disabled={buttonDisable}
					type="primary"
					shape="round"
					onClick={showDrawer}
					style={{ marginRight: '15px' }}
					// icon={<PlusOutlined />}
				>
					ADD SECTIONS
				</Button>
				{/* <SectionPager handleChange={this.handleChange} /> */}
			</Row>
			<DndProvider backend={HTML5Backend}>
				<Table
					dataSource={section}
					columns={columns}
					components={components}
					pagination={false}
					rowKey={record => record.sectionID}
					onRow={(record, index) => ({
						index: section.findIndex(item => item.sectionID === record.sectionID),
						moveRow: moveRow,
						onDoubleClick: () => {
							displayDrawer(record);
						},
					})}
				/>
			</DndProvider>
			<Drawer
				title={drawerState.drawerTitle}
				width="30%"
				visible={drawerState.visible}
				onClose={onDrawerClose}
				destroyOnClose
			>
				<SectionForm
					buttonNames={drawerState.buttonNames}
					onDrawerClose={onDrawerClose}
					selecetedData={drawerState.selecetedData}
					onSuccessCreate={onSuccessCreate}
					onSuccessUpdate={onSuccessUpdate}
					maxSectionSort={Math.max(...section.map(s => s.sectionSort || 0), 0)}
				/>
			</Drawer>
		</>
	);
}

export default Section;
